<div class="authentication-wrapper">
    <div class="container">
        <div class="row justify-content-center">
            <div class="col-lg-6 col-md-8 col-sm-10">
                <!-- Loading State -->
                <div class="card shadow-lg" *ngIf="isValidatingToken">
                    <div class="card-body text-center py-5">
                        <i class="fa fa-spinner fa-spin fa-3x text-violet mb-3"></i>
                        <h5>Validating your setup link...</h5>
                        <p class="text-muted">Please wait</p>
                    </div>
                </div>

                <!-- Token Invalid/Expired State -->
                <div class="card shadow-lg" *ngIf="!isValidatingToken && !tokenValid && !setupComplete">
                    <div class="card-body text-center py-5">
                        <i class="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                        <h4 class="mb-3">Setup Link {{ tokenExpired ? 'Expired' : 'Invalid' }}</h4>
                        <p class="text-muted mb-4">{{ tokenError }}</p>

                        <div class="alert alert-light border" role="alert">
                            <h6 class="alert-heading">
                                <i class="fa fa-info-circle me-2"></i>What should I do?
                            </h6>
                            <ul class="text-start mb-0">
                                <li *ngIf="tokenExpired">Setup links expire after 7 days for security reasons</li>
                                <li>Contact your administrator to resend the invitation</li>
                                <li>Check your email for a newer setup link</li>
                            </ul>
                        </div>

                        <button class="btn btn-violet mt-3" (click)="requestNewLink()">
                            <i class="fa fa-envelope me-2"></i>Request New Link
                        </button>
                    </div>
                </div>

                <!-- Setup Form State -->
                <div class="card shadow-lg" *ngIf="!isValidatingToken && tokenValid && !setupComplete">
                    <div class="card-header bg-violet text-white text-center py-4">
                        <i class="fa fa-user-check fa-2x mb-2"></i>
                        <h4 class="mb-0">Complete Your Account Setup</h4>
                    </div>

                    <div class="card-body p-4">
                        <!-- Welcome Message -->
                        <div class="alert alert-info mb-4">
                            <h6 class="mb-2">Welcome{{ userName ? ', ' + userName : '' }}!</h6>
                            <p class="mb-0 small">
                                <i class="fa fa-envelope me-2"></i>{{ userEmail }}
                            </p>
                        </div>

                        <p class="text-muted mb-4">
                            {{ userType === 'admin' ? 'Create a new secure password for your account.' : 'Please enter the temporary password from your email, then create a new secure password for your account.' }}
                        </p>

                        <!-- Setup Form -->
                        <form [formGroup]="setupForm" (ngSubmit)="onSubmit()">
                            <!-- Temporary Password Field -->
                            <div class="mb-3" *ngIf="userType !== 'admin'">
                                <label class="form-label fw-bold">Temporary Password <span class="text-danger">*</span></label>
                                <div class="input-group">
                                    <input
                                        [type]="hideTemporaryPassword ? 'password' : 'text'"
                                        class="form-control"
                                        formControlName="temporaryPassword"
                                        placeholder="Enter temporary password from email"
                                        [class.is-invalid]="f['temporaryPassword'].invalid && f['temporaryPassword'].touched">
                                    <button
                                        class="btn btn-outline-secondary"
                                        type="button"
                                        (click)="hideTemporaryPassword = !hideTemporaryPassword">
                                        <i [class]="hideTemporaryPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                                    </button>
                                </div>
                                <small class="text-muted d-block mt-1">
                                    <i class="fa fa-info-circle me-1"></i>Check your email for the temporary password
                                </small>
                            </div>

                            <!-- New Password Field -->
                            <div class="mb-3">
                                <label class="form-label fw-bold">New Password <span class="text-danger">*</span></label>
                                <div class="input-group">
                                    <input
                                        [type]="hidePassword ? 'password' : 'text'"
                                        class="form-control"
                                        formControlName="password"
                                        placeholder="Enter password"
                                        maxlength="16"
                                        [class.is-invalid]="f['password'].invalid && f['password'].touched">
                                    <button
                                        class="btn btn-outline-secondary"
                                        type="button"
                                        (click)="hidePassword = !hidePassword">
                                        <i [class]="hidePassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                                    </button>
                                </div>

                                <!-- Password Requirements -->
                                <div class="password-requirements mt-2">
                                    <small class="d-block mb-1 fw-bold text-muted">Password must contain:</small>
                                    <small class="d-block" [class.text-success]="passwordRequirements.minLength && passwordRequirements.maxLength" [class.text-muted]="!passwordRequirements.minLength || !passwordRequirements.maxLength">
                                        <i [class]="passwordRequirements.minLength && passwordRequirements.maxLength ? 'fa fa-check-circle' : 'fa fa-circle-o'"></i>
                                        8-16 characters
                                    </small>
                                    <small class="d-block" [class.text-success]="passwordRequirements.hasUpperCase" [class.text-muted]="!passwordRequirements.hasUpperCase">
                                        <i [class]="passwordRequirements.hasUpperCase ? 'fa fa-check-circle' : 'fa fa-circle-o'"></i>
                                        One uppercase letter (A-Z)
                                    </small>
                                    <small class="d-block" [class.text-success]="passwordRequirements.hasLowerCase" [class.text-muted]="!passwordRequirements.hasLowerCase">
                                        <i [class]="passwordRequirements.hasLowerCase ? 'fa fa-check-circle' : 'fa fa-circle-o'"></i>
                                        One lowercase letter (a-z)
                                    </small>
                                    <small class="d-block" [class.text-success]="passwordRequirements.hasNumber" [class.text-muted]="!passwordRequirements.hasNumber">
                                        <i [class]="passwordRequirements.hasNumber ? 'fa fa-check-circle' : 'fa fa-circle-o'"></i>
                                        One number (0-9)
                                    </small>
                                    <small class="d-block" [class.text-success]="passwordRequirements.hasSpecialChar" [class.text-muted]="!passwordRequirements.hasSpecialChar">
                                        <i [class]="passwordRequirements.hasSpecialChar ? 'fa fa-check-circle' : 'fa fa-circle-o'"></i>
                                        One special character (-+_!&#64;#$%^&*., ?)
                                    </small>
                                </div>
                            </div>

                            <!-- Confirm Password Field -->
                            <div class="mb-4">
                                <label class="form-label fw-bold">Confirm Password <span class="text-danger">*</span></label>
                                <div class="input-group">
                                    <input
                                        [type]="hideConfirmPassword ? 'password' : 'text'"
                                        class="form-control"
                                        formControlName="confirmPassword"
                                        placeholder="Re-enter password"
                                        maxlength="16"
                                        [class.is-invalid]="(f['confirmPassword'].invalid && f['confirmPassword'].touched) || (setupForm.errors?.['passwordMismatch'] && f['confirmPassword'].touched)">
                                    <button
                                        class="btn btn-outline-secondary"
                                        type="button"
                                        (click)="hideConfirmPassword = !hideConfirmPassword">
                                        <i [class]="hideConfirmPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                                    </button>
                                </div>
                                <div class="invalid-feedback d-block" *ngIf="setupForm.errors?.['passwordMismatch'] && f['confirmPassword'].touched">
                                    Passwords do not match
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <div class="d-grid">
                                <button
                                    type="submit"
                                    class="btn btn-violet btn-lg"
                                    [disabled]="isSubmitting || setupForm.invalid || !isPasswordValid()">
                                    <span *ngIf="!isSubmitting">
                                        <i class="fa fa-check me-2"></i>Complete Setup
                                    </span>
                                    <span *ngIf="isSubmitting">
                                        <i class="fa fa-spinner fa-spin me-2"></i>Setting up your account...
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>

                    <div class="card-footer text-center bg-light">
                        <small class="text-muted">
                            <i class="fa fa-lock me-1"></i>Your password is encrypted and secure
                        </small>
                    </div>
                </div>

                <!-- Setup Complete State -->
                <div class="card shadow-lg" *ngIf="setupComplete">
                    <div class="card-body text-center py-5">
                        <i class="fa fa-check-circle fa-4x text-success mb-3"></i>
                        <h4 class="text-success mb-3">Account Setup Complete!</h4>
                        <p class="text-muted mb-4">
                            Your account has been successfully activated. You can now log in with your email and password.
                        </p>

                        <div class="alert alert-light border" role="alert">
                            <p class="mb-0">
                                <i class="fa fa-info-circle me-2"></i>
                                Redirecting you to the sign-in page...
                            </p>
                        </div>

                        <div class="spinner-border text-violet mt-3" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
